<template lang="pug">
SideOverlaySlot(
  :isOpen="isOpen",
  :title="overlayTitle",
  @closeOverlay="closeOverlay"
)
  template(#contentTop)
    section.patient-section
      template(v-if="patient")
        p <strong>Patient: {{ getUserFormatted(patient) }}</strong>
        p <strong>DOB: {{ getDobFormatted(patient.dob, "/") }}</strong>
      template(v-else)
        p No patient selected.
    form#add-new-referral(
      @submit.prevent="submitReferral",
      novalidate
    )
      .columns
        .column
          .form-group
            label(for="provider-location") Provider/Location
            input#provider-location(
              type="text"
              v-model="form.providerLocation"
              placeholder="Enter provider or location"
            )
      .columns.is-variable
        .column.is-half
          .form-group
            label(for="appointment-date") Appointment Date
            DatePicker(
              v-model="appointmentDateInternal"
              :masks="dateMasks"
            )
              template(v-slot="{ inputValue, inputEvents }")
                input(
                  :value="inputValue"
                  v-on="inputEvents"
                  placeholder="MM/DD/YYYY"
                )
        .column.is-half
          TimeDropdown(
            @timeResults="timeResults"
          )
      .columns
        .column
          .form-group
            label(for="referral-type") Referral Type
            select#referral-type(v-model="form.referralType")
              option(value="" disabled) Please Select
              option(
                v-for="type in referralTypes"
                :key="type.id"
                :value="type.id"
              ) {{ type.name }}
      .columns
        .column
          .form-group
            label(for="referral-attempt") Referral Attempt
            select#referral-attempt(v-model="form.referralAttempt")
              option(value="" disabled) Please Select
              option(value="new-referral") New Referral
              option(value="first-reschedule") 1st Reschedule
              option(value="second-reschedule") 2nd Reschedule
      .columns
        .column.is-half
          .form-group
            label(for="insurance-options") Insurance Options
            select#insurance-options(v-model="form.insuranceOption")
              option(value="" disabled) Please Select
              option(value="Insured") Insured
              option(value="Uninsured") Uninsured
        .column.is-half(v-if="form.insuranceOption === 'Insured'")
          .form-group
            label(for="insured-type") Insured Type
            select#insured-type(v-model="form.insuredType")
              option(value="" disabled) Please Select
              option(value="Private/Employer") Private/Employer
              option(value="Medicare/Medicaid") Medicare/Medicaid
      .columns
        .column
          .form-group
            label(for="notes") Notes
            textarea#notes(v-model="form.notes" rows="4" placeholder="Add notes")

  template(#contentBottom)
    .buttons-row.flex.flex--space-between
      BaseBtn.x-lg.red-outline(
        href="#",
        @click.prevent="closeOverlay"
      ) CANCEL
      BaseBtn.x-lg.green(
        type="submit",
        form="add-new-referral"
        :disabled="isSubmitting || !isFormValid"
        :class="{ spinner: isSubmitting }"
      ) CONTINUE
</template>

<script>
import { defineComponent } from "vue";
import moment from "moment";
import { DatePicker } from "v-calendar";
import SideOverlaySlot from "@/components/overlays/SideOverlaySlot.vue";
import TimeDropdown from "@/components/TimeDropdown.vue";
import { getDobFormatted, getUserFormatted } from "@/helpers";
import { fetchPatientNavigationSettings } from "@/api/patientNavigationSettingsApi";
import {
  createPatientReferral,
  reschedulePatientNavigation,
  getPatientNavigationReferral,
} from "@/api/patientNavigation";
import { createToast } from "mosha-vue-toastify";
import { apiStatus, apiStatusComputedFactory } from "@/api";

const { IDLE, PENDING, SUCCESS, ERROR } = apiStatus;

export default defineComponent({
  name: "SchedulePatientOverlay",
  components: {
    SideOverlaySlot,
    DatePicker,
    TimeDropdown,
  },
  computed: {
    ...apiStatusComputedFactory(["createPatientNavigationStatus"]),
    isRescheduleMode() {
      return this.$route.query.reschedule === "true";
    },
    overlayTitle() {
      return this.isRescheduleMode ? "RESCHEDULE" : "ADD NEW REFERRAL";
    },
    rescheduleAttemptValue() {
      if (!this.isRescheduleMode) return "new-referral";
      const rescheduleType = this.$route.query.rescheduleType;
      return rescheduleType === "1st"
        ? "first-reschedule"
        : "second-reschedule";
    },
    isSubmitting() {
      return this.createPatientNavigationStatus === PENDING;
    },
    isFormValid() {
      if (!this.patient || !this.patient.id) return false;
      if (!this.form.referralType) return false;
      if (!this.form.referralAttempt) return false;
      if (!this.form.insuranceOption) return false;
      if (this.form.insuranceOption === "Insured" && !this.form.insuredType) {
        return false;
      }
      return true;
    },
  },
  emits: ["closeSelectPatientOverlay"],
  props: {
    isOpen: {
      type: Boolean,
      default: false,
    },
    patient: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      dateMasks: {
        input: "MM/DD/YYYY",
      },
      form: {
        providerLocation: "",
        appointmentDate: "",
        appointmentTime: "",
        referralType: "",
        referralAttempt: "",
        insuranceOption: "",
        insuredType: "",
        notes: "",
      },
      appointmentDateInternal: null,
      referralTypes: [],
      createPatientNavigationStatus: IDLE,
      originalAppointment: null,
    };
  },
  watch: {
    "form.insuranceOption"(value) {
      if (value !== "Insured") {
        this.form.insuredType = "";
      }
    },
    isOpen(value) {
      if (value) {
        this.loadReferralTypes();
        if (this.isRescheduleMode) {
          this.loadOriginalAppointment();
        } else {
          this.form.referralAttempt = "new-referral";
        }
      }
    },
    appointmentDateInternal(value) {
      if (!value) {
        this.form.appointmentDate = "";
        return;
      }
      this.form.appointmentDate = moment(value).format("MM/DD/YYYY");
    },
  },
  methods: {
    getDobFormatted,
    getUserFormatted,
    timeResults(val) {
      this.form.appointmentTime = val;
    },
    async loadReferralTypes() {
      try {
        const { data } = await fetchPatientNavigationSettings({
          params: {
            size: 100,
            filters: {
              disabled: false,
            },
            order: {
              by: "patient-navigation-name",
              direction: "asc",
            },
          },
        });
        this.referralTypes = data.patientNavigationSettings || [];
      } catch (error) {
        console.log(error);
        this.referralTypes = [];
      }
    },

    async loadOriginalAppointment() {
      const originalId = this.$route.query.originalId;
      if (!originalId) return;

      try {
        const { data } = await getPatientNavigationReferral(originalId);
        this.originalAppointment = data.patientNavigation;

        // Pre-populate form with original appointment data
        if (this.originalAppointment) {
          this.form.providerLocation =
            this.originalAppointment.providerLocation || "";
          this.form.referralType =
            this.originalAppointment.idPatientNavigationSetting?.toString() ||
            "";
          this.form.referralAttempt = this.rescheduleAttemptValue;
          this.form.insuranceOptions =
            this.originalAppointment.insuranceOptions || "";
          this.form.notes = this.originalAppointment.notes || "";

          // Parse insurance options
          if (this.originalAppointment.insuranceOptions) {
            if (
              this.originalAppointment.insuranceOptions.includes(
                "Private/Employer"
              )
            ) {
              this.form.insuranceOption = "Insured";
              this.form.insuredType = "Private/Employer";
            } else if (
              this.originalAppointment.insuranceOptions.includes(
                "Medicare/Medicaid"
              )
            ) {
              this.form.insuranceOption = "Insured";
              this.form.insuredType = "Medicare/Medicaid";
            } else if (
              this.originalAppointment.insuranceOptions === "Uninsured"
            ) {
              this.form.insuranceOption = "Uninsured";
            }
          }
        }
      } catch (error) {
        console.error("Error loading original appointment:", error);
        createToast("Error loading original appointment data.", {
          timeout: 4000,
          type: "danger",
          position: "bottom-right",
        });
      }
    },
    closeOverlay() {
      this.resetForm();
      this.$emit("closeSelectPatientOverlay");
    },
    getInsuranceOptionValue() {
      if (this.form.insuranceOption === "Insured") {
        if (this.form.insuredType === "Private/Employer") {
          return "Insured - Private/Employer";
        }
        if (this.form.insuredType === "Medicare/Medicaid") {
          return "Insured - Medicare/Medicaid";
        }
        return null;
      }

      if (this.form.insuranceOption === "Uninsured") {
        return "Uninsured";
      }

      return null;
    },
    getScheduledAtUnix() {
      if (this.appointmentDateInternal) {
        return moment(this.appointmentDateInternal).unix();
      }
      if (this.form.appointmentDate) {
        const parsed = moment(this.form.appointmentDate, "MM/DD/YYYY", true);
        if (parsed.isValid()) {
          return parsed.unix();
        }
      }
      return null;
    },
    async submitReferral() {
      if (!this.isFormValid) {
        createToast("Please complete the required fields before continuing.", {
          timeout: 4000,
          type: "danger",
          position: "bottom-right",
        });
        return;
      }

      const idCommunity = Number(this.$route.params.idCommunity) || null;
      const insuranceOptions = this.getInsuranceOptionValue();

      if (this.form.insuranceOption === "Insured" && !insuranceOptions) {
        createToast("Please select an insured type.", {
          timeout: 4000,
          type: "danger",
          position: "bottom-right",
        });
        return;
      }

      this.createPatientNavigationStatus = PENDING;

      try {
        if (this.isRescheduleMode) {
          await this.handleRescheduleSubmission(insuranceOptions);
        } else {
          await this.handleNewReferralSubmission(idCommunity, insuranceOptions);
        }

        this.createPatientNavigationStatus = SUCCESS;
        this.closeOverlay();

        // Redirect to patient navigation all outstanding tab
        this.$router.push({
          name: "cco-patient-navigation.patient-navigation.all_outstanding",
          params: { idCommunity },
        });
      } catch (error) {
        console.error(error);
        this.createPatientNavigationStatus = ERROR;
        createToast(
          error?.response?.data?.message ||
            (this.isRescheduleMode
              ? "There was a problem rescheduling the appointment."
              : "There was a problem creating the referral."),
          {
            timeout: 4000,
            type: "danger",
            position: "bottom-right",
          }
        );
      } finally {
        if (this.createPatientNavigationStatus !== SUCCESS) {
          this.createPatientNavigationStatus = IDLE;
        }
      }
    },

    async handleRescheduleSubmission(insuranceOptions) {
      const originalId = this.$route.query.originalId;
      if (!originalId) {
        throw new Error("Original appointment ID not found");
      }

      const rescheduleData = {
        scheduledAt: this.getScheduledAtUnix(),
        providerLocation: this.form.providerLocation || null,
        notes: this.form.notes || null,
        timeOfAppointment: this.form.appointmentTime || null,
        insuranceOptions,
        idPatientNavigationSetting: this.form.referralType
          ? Number(this.form.referralType)
          : null,
        referralAttempt: this.form.referralAttempt || null,
      };

      await reschedulePatientNavigation(originalId, rescheduleData);
    },

    async handleNewReferralSubmission(idCommunity, insuranceOptions) {
      const payload = {
        idPatient: this.patient?.id || null,
        idCommunity,
        notes: this.form.notes || null,
        providerLocation: this.form.providerLocation || null,
        timeOfAppointment: this.form.appointmentTime || null,
        insuranceOptions,
        idPatientNavigationSetting: this.form.referralType
          ? Number(this.form.referralType)
          : null,
        referralType: this.form.referralType
          ? Number(this.form.referralType)
          : null,
        referralAttempt: this.form.referralAttempt || null,
        scheduledAt: this.getScheduledAtUnix(),
      };

      if (!payload.idPatient || !payload.idCommunity) {
        throw new Error("Missing patient or community context");
      }

      await createPatientReferral(payload);
    },
    resetForm() {
      this.form.providerLocation = "";
      this.form.appointmentDate = "";
      this.form.appointmentTime = "";
      this.form.referralType = "";
      this.form.referralAttempt = "";
      this.form.insuranceOption = "";
      this.form.insuredType = "";
      this.form.notes = "";
      this.appointmentDateInternal = null;
      this.createPatientNavigationStatus = IDLE;
      if (this.referralTypes.length === 0) {
        this.loadReferralTypes();
      }
    },
  },
  created() {
    this.loadReferralTypes();
  },
});
</script>

<style scoped lang="scss">
.patient-section {
  margin-bottom: 24px;
  padding: 37px 24px 25px;
  background-color: $primaryBgBlue;
  p {
    margin: 0 0 8px;
    font-size: 21px;
    font-weight: 500;
    line-height: 1.1;
    color: $primaryLightBlue;
    overflow: hidden;
    strong {
      font-weight: 700;
    }
  }
}

.buttons-row {
  margin-top: 24px;
  gap: 16px;
}

form#add-new-referral {
  margin: 47px 0 43px;
  .form-group textarea {
    resize: vertical;
    min-height: 120px;
  }
}
</style>
